import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Grid2 as Grid,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  TextFields as TextIcon,
  Numbers as NumberIcon,
  ToggleOn as BooleanIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSConfig } from '../../services';

const CMSSettings: React.FC = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<CMSConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<Partial<CMSConfig> | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cmsService.getConfig();
      setConfigs(response.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingSettings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEdit = (config: CMSConfig) => {
    setEditData(config);
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData?.chave) return;

    setSaving(true);
    setError(null);
    try {
      await cmsService.updateConfig(editData.chave, {
        valor: editData.valor,
        valor_en: editData.valor_en,
        valor_fr: editData.valor_fr,
        tipo: editData.tipo,
        descricao: editData.descricao,
      });
      setSuccess(t('cms.configUpdated'));
      setEditDialog(false);
      setEditData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingConfig'));
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (tipo: string) => {
    switch (tipo) {
      case 'texto': return <TextIcon fontSize="small" />;
      case 'numero': return <NumberIcon fontSize="small" />;
      case 'boolean': return <BooleanIcon fontSize="small" />;
      case 'json': return <CodeIcon fontSize="small" />;
      default: return <SettingsIcon fontSize="small" />;
    }
  };

  const getTypeColor = (tipo: string) => {
    switch (tipo) {
      case 'texto': return 'primary';
      case 'numero': return 'info';
      case 'boolean': return 'success';
      case 'json': return 'warning';
      default: return 'default';
    }
  };

  const formatValue = (config: CMSConfig) => {
    if (!config.valor) return '-';

    switch (config.tipo) {
      case 'boolean':
        return config.valor === 'true' || config.valor === '1' ? t('common.yes') : t('common.no');
      case 'json':
        try {
          const parsed = JSON.parse(config.valor);
          return JSON.stringify(parsed, null, 2).substring(0, 50) + '...';
        } catch {
          return config.valor.substring(0, 50) + '...';
        }
      default:
        return config.valor.length > 50 ? config.valor.substring(0, 50) + '...' : config.valor;
    }
  };

  // Group configs by category (based on key prefix)
  const groupedConfigs = configs.reduce((acc, config) => {
    const parts = config.chave.split('_');
    const category = parts.length > 1 ? parts[0].toUpperCase() : t('cms.general').toUpperCase();
    if (!acc[category]) acc[category] = [];
    acc[category].push(config);
    return acc;
  }, {} as Record<string, CMSConfig[]>);

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.settingsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.settingsSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {t('cms.noConfigFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('cms.configAutoCreated')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedConfigs).map(([category, categoryConfigs]) => (
          <Card key={category} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {category}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('cms.key')}</TableCell>
                      <TableCell>{t('common.type')}</TableCell>
                      <TableCell>{t('cms.valueLabel')}</TableCell>
                      <TableCell>{t('common.description')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryConfigs.map((config) => (
                      <TableRow key={config.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                            {config.chave}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={getTypeIcon(config.tipo)}
                            label={config.tipo}
                            color={getTypeColor(config.tipo) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily={config.tipo === 'json' ? 'monospace' : 'inherit'}>
                            {formatValue(config)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {config.descricao || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('common.edit')}>
                            <IconButton size="small" onClick={() => handleOpenEdit(config)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))
      )}

      {/* Stats */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('common.total')}</Typography>
              <Typography variant="h4" fontWeight="bold">{configs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.text')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {configs.filter(c => c.tipo === 'texto').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.number')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {configs.filter(c => c.tipo === 'numero').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.boolean')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {configs.filter(c => c.tipo === 'boolean').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('cms.editConfig')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.key')}
                value={editData?.chave || ''}
                disabled
                InputProps={{
                  style: { fontFamily: 'monospace' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={editData?.tipo || 'texto'}
                onChange={(e) => setEditData(prev => ({ ...prev, tipo: e.target.value }))}
              >
                <MenuItem value="texto">{t('cms.text')}</MenuItem>
                <MenuItem value="numero">{t('cms.number')}</MenuItem>
                <MenuItem value="boolean">{t('cms.boolean')}</MenuItem>
                <MenuItem value="json">{t('cms.json')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={editData?.descricao || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder={t('cms.configDescriptionPlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              {editData?.tipo === 'boolean' ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editData?.valor === 'true' || editData?.valor === '1'}
                      onChange={(e) => setEditData(prev => ({ ...prev, valor: e.target.checked ? 'true' : 'false' }))}
                    />
                  }
                  label={editData?.valor === 'true' || editData?.valor === '1' ? t('cms.enabled') : t('cms.disabled')}
                />
              ) : editData?.tipo === 'numero' ? (
                <TextField
                  fullWidth
                  type="number"
                  label={t('cms.valueLabel')}
                  value={editData?.valor || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, valor: e.target.value }))}
                />
              ) : (
                <Box>
                  {/* PT */}
                  <Box mb={2}>
                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>🇨🇻 Português (original)</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={editData?.tipo === 'json' ? 8 : 4}
                      value={editData?.valor || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, valor: e.target.value }))}
                      InputProps={editData?.tipo === 'json' ? { style: { fontFamily: 'monospace' } } : undefined}
                      helperText={editData?.tipo === 'json' ? t('cms.validJson') : undefined}
                    />
                  </Box>
                  {/* EN */}
                  <Box mb={2}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>🇬🇧 English</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={editData?.tipo === 'json' ? 8 : 4}
                      placeholder="Translate here..."
                      value={editData?.valor_en || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, valor_en: e.target.value }))}
                      InputProps={editData?.tipo === 'json' ? { style: { fontFamily: 'monospace' } } : undefined}
                    />
                  </Box>
                  {/* FR */}
                  <Box>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>🇫🇷 Français</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={editData?.tipo === 'json' ? 8 : 4}
                      placeholder="Traduisez ici..."
                      value={editData?.valor_fr || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, valor_fr: e.target.value }))}
                      InputProps={editData?.tipo === 'json' ? { style: { fontFamily: 'monospace' } } : undefined}
                    />
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSSettings;
